import React from "react";
import { ColumnLayout, Spinner } from "@amzn/awsui-components-react-v3";
import { FieldSchemaDto } from "@amzn/crowd-front-end-common-library";
import PersonRenderer from "../../common/PersonRenderer";
import styles from "./static-field-renderer.module.css";
import { AutoSuggestItem } from "../../search/AutoSuggestResponse";
import { MetricsState } from "../../logging";
import StaticTeamRenderer from "../../common/StaticTeamRenderer";
import { useHttpRequest, LoadingState } from "../../http";
import BuildTeamPath from "../../common/utils/BuildTeamPath";
import BreadcrumbItem from "../../common/breadcrumbs/BreadcrumbItem";

interface StaticFieldProps {
  field: FieldSchemaDto;
  formValues: any;
  metrics?: MetricsState;
}

const renderPeople = (value: AutoSuggestItem[]): JSX.Element[] => {
  return value.map((person, i) => (
    <PersonRenderer employee={person} showTitle />
  ));
};

const TeamRenderer = (teamId: string, metrics: MetricsState): JSX.Element => {
  // retrieve the breadcrumbs for the current team.
  const {
    loadingState: targetLoadingState,
    data: response,
    error: targetFetchError,
  } = useHttpRequest("moveTeamGetBreadcrumbs", metrics, {
    url: `/api/v1/teams/${teamId}/ancestors?includeCurrentTeam=true`,
    method: "GET",
  });

  if (targetFetchError) {
    if (targetFetchError.response && targetFetchError.response.status === 404) {
      throw new Error(
        `Unable to resolve team with id: ${teamId}.  Redirecting to home page.`
      );
    } else {
      throw targetFetchError;
    }
  }

  const ancestors =
    targetLoadingState === LoadingState.RESOLVED
      ? (response as any).data
      : undefined;

  if (!ancestors) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }

  const breadcrumbs: BreadcrumbItem[] = ancestors.map(
    (ancestor: { name: string }) => ({ name: ancestor.name })
  );

  const teamName: string = breadcrumbs[breadcrumbs.length - 1].name || "";
  const teamPath = BuildTeamPath(breadcrumbs, true);

  return <StaticTeamRenderer teamName={teamName} teamPath={teamPath} />;
};

const StaticFieldRenderer: React.FC<StaticFieldProps> = ({
  field,
  formValues,
  metrics,
}): JSX.Element => {
  const { propertyType, propertyName } = field;
  const value = formValues[propertyName];
  if (!value) {
    return <div className={styles["info-placeholder"]}>-</div>;
  }
  switch (propertyType) {
    case "employee":
      return <PersonRenderer employee={value} />;
    case "multiselect<employee>":
      return (
        <ColumnLayout>
          <div data-awsui-column-layout-root="true">{renderPeople(value)}</div>
        </ColumnLayout>
      );
    case "team":
      return TeamRenderer(value, metrics as MetricsState);
    case "select<string>":
    case "select<number>":
    case "string":
    case "number":
    case "text":
      return <div>{value}</div>;
    default:
      throw new Error(`Invalid input type: "${propertyType}".`);
  }
};

export default StaticFieldRenderer;
